import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, m as motion, motionValue, useMotionValue, useTransform, } from 'framer-motion';
import styled from '@emotion/styled';
import { css, ThemeProvider } from '@emotion/react';
import PointerReplace, { Directions } from './GalleryPointer';
import { calculateOffset, useOffsetFromIndex, useTransition, } from '@polestar/component-warehouse-react/components/gallery/Gallery.helpers';
import GallerySlides from './GallerySlides';
import { createCSSTransition, createTransition, MotionDurationsSeconds, MotionEasings, Transitions, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { useReducedMotion } from '@polestar/component-warehouse-react/hooks/useReducedMotion';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
import docgenInterface from '@polestar/component-warehouse-react/helpers/docgenInterface';
import { GalleryVariants, } from '@polestar/component-warehouse-react/components/gallery/Gallery';
import LayoutIndexes from '@polestar/component-warehouse-react/tokens/layout-indexes/LayoutIndexes';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import useMutableValue, { mutableValue, } from '@polestar/component-warehouse-react/hooks/useMutableValue';
import { clamp } from '@polestar/component-warehouse-react/helpers/mathematical';
import { useScrollContext } from '@polestar/component-warehouse-react/components/scroll-observer/ScrollObserver';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import IconButton from '@polestar/component-warehouse-react/components/icon-button/IconButton';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import { MotionControlsVariants } from '@polestar/component-warehouse-react/components/motion/useMotionControls';
import Opacities from '@polestar/component-warehouse-react/tokens/opacities/Opacities';
import GalleryIndicator from './GalleryIndicator';
import Themes from '@polestar/component-warehouse-react/themes/Themes';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import { preventCancelableDefault } from '@polestar/component-warehouse-react/helpers/event';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
var GalleryAnimationDirection;
(function (GalleryAnimationDirection) {
    GalleryAnimationDirection[GalleryAnimationDirection["forward"] = 1] = "forward";
    GalleryAnimationDirection[GalleryAnimationDirection["backward"] = -1] = "backward";
})(GalleryAnimationDirection || (GalleryAnimationDirection = {}));
export const GalleryControlledContentPropsInterface = docgenInterface();
const initialDragThreshold = 0.03; // 3%
const EMPTY_ARRAY = [];
const arrowAnimationVariants = {
    [MotionControlsVariants.enter]: {
        opacity: Opacities.none,
        scale: 0,
    },
    [MotionControlsVariants.visible]: {
        opacity: Opacities.full,
        scale: 1,
        transition: createTransition({
            duration: MotionDurationsSeconds.xSlow,
            ease: MotionEasings.easeInOutSoft,
        }),
    },
};
const GalleryControlled = ({ onChange, content: originalContent = EMPTY_ARRAY, CustomComponent, clampedIndex: _clampedIndex, internalIndex: _internalIndex, variant: _variant = GalleryVariants.standard, dragThreshold, arrowLeftButtonProps, arrowRightButtonProps, hasIndicator = true, ariaLabels, }) => {
    var _a, _b, _c;
    const { isRTL } = useDirection();
    const variant = CustomComponent
        ? GalleryVariants.standard || GalleryVariants.highContrastChevrons
        : _variant;
    const isContainedVariant = variant === GalleryVariants.contained;
    const originalContentLength = originalContent.length;
    const content = useMemo(() => {
        const items = isRTL ? [...originalContent].reverse() : originalContent;
        const value = (originalContent === null || originalContent === void 0 ? void 0 : originalContent.length) > 1
            ? // GalleryHelpers.offsetFromIndex needs to change if this is changed
                [...items, ...items, ...(variant === GalleryVariants.contained ? items : [])]
            : items;
        if (isRTL) {
            // due to reversing the content order in RTL, adjust to ensure first element in orignalContent starts
            return [
                ...value.slice(originalContent.length - 1),
                ...value.slice(0, originalContent.length - 1),
            ];
        }
        return value;
    }, [originalContent, variant, isRTL]);
    const duplicateContentLength = content.length;
    const internalIndex = useMemo(() => _internalIndex || motionValue(0), [_internalIndex]);
    const clampedIndex = useMemo(() => _clampedIndex || mutableValue(0), [_clampedIndex]);
    // Content slide indicator/button theme
    const initialDarkTheme = (_a = content[internalIndex.get()]) === null || _a === void 0 ? void 0 : _a.isDark;
    const [isDarkTheme, setIsDarkTheme] = useState(initialDarkTheme || false);
    // Catch updates of content, like when toggling mode and the content array is replaced
    useEffect(() => {
        var _a;
        const isDark = (_a = content[clampedIndex.get()]) === null || _a === void 0 ? void 0 : _a.isDark;
        if (isDark !== undefined) {
            setIsDarkTheme(isDark);
        }
        else {
            setIsDarkTheme(false);
        }
    }, [clampedIndex, content]);
    useEffect(() => {
        // necessary to support dragging & click interaction
        clampedIndex.on('change', (val) => {
            var _a;
            const isDark = (_a = content[val]) === null || _a === void 0 ? void 0 : _a.isDark;
            if (isDark !== undefined) {
                setIsDarkTheme(isDark);
            }
            else {
                setIsDarkTheme(false);
            }
        });
    }, [clampedIndex, content]);
    const currentPage = useRef(0);
    const wrapperWidth = useMotionValue(0);
    const dragged = useRef(false);
    const directlySet = useRef(false);
    const { isKeyboardControlled } = useInteractionMode();
    const isReducedMotion = useReducedMotion();
    const isDisabledMotion = isKeyboardControlled || isReducedMotion;
    /**
     * In GalleryVariants.contained useful to move the starting position to align with the Grid.
     */
    const positionOffset = useMotionValue(0);
    /**
     * The amount of distance (in px) between the current position and that target position. For example when dragged to not start the movement from point 0, but the current (dragged) offset.
     */
    const offset = useMotionValue(0);
    const velocity = useMotionValue(0);
    const percentages = variant === GalleryVariants.standard ||
        variant === GalleryVariants.highContrastChevrons ||
        !!CustomComponent;
    const [initialized, setInitialized] = useState(percentages);
    const [contentLoaded, setContentLoaded] = useState(false);
    /**
     * Measure the width per slide to use for horizontal aligning all slides next to each other.
     */
    const slideWidths = useMutableValue(percentages ? Array(duplicateContentLength).fill(0) : []);
    // The destination transform of the slider container
    const { createOffsetFromIndex, totalOffset } = useOffsetFromIndex(slideWidths, isContainedVariant, isRTL);
    // Prevent looping
    // @ts-ignore Overload matching isn't correct
    const minWrapperX = useTransform([positionOffset, wrapperWidth, slideWidths], ([latestPositionOffset, latestWrapperWidth, latestSlideWidths]) => {
        const totalGalleryWidth = latestSlideWidths.reduce((value, item) => value + item, 0);
        return latestWrapperWidth - totalGalleryWidth - latestPositionOffset;
    });
    const clampInternalIndex = false;
    const wrapperX = useTransform(
    // @ts-ignore Overload matching isn't correct
    [offset, internalIndex, positionOffset, minWrapperX], ([latestOffset, latestInternalIndex, latestPositionOffset, latestMinWrapperX]) => {
        if (percentages) {
            // change the movement direction depending on the Directions mode for % positioning
            return (isRTL ? -1 : 1) * (-latestInternalIndex + latestOffset) * 100;
        }
        const positionOffset = isRTL ? -latestPositionOffset : latestPositionOffset;
        // calculate for the contained variant based on image widths / offsets
        const x = (isRTL && !isContainedVariant ? 1 : -1) * createOffsetFromIndex(latestInternalIndex) +
            latestOffset +
            positionOffset;
        if (!clampInternalIndex) {
            return x;
        }
        return clamp(clamp(x, latestMinWrapperX, latestPositionOffset), latestMinWrapperX, positionOffset);
    });
    // @ts-ignore Overload matching isn't correct
    const canNavigateBackward = useTransform([wrapperX, positionOffset], ([latestWrapperX, latestPositionOffset]) => !clampInternalIndex || parseFloat(latestWrapperX) < latestPositionOffset);
    // @ts-ignore Overload matching isn't correct
    const canNavigateForward = useTransform([wrapperX, minWrapperX], ([latestWrapperX, latestMinWrapperX]) => !clampInternalIndex || parseFloat(latestWrapperX) > latestMinWrapperX);
    /**
     * Subscribe to wrapperX for end value of slides position
     * Animate when not dragging, otherwise directly set the new value
     */
    const { animatedMotionValue: x, directlySetOnce, disableDirectlySetOnce, } = useTransition(wrapperX, Transitions.largeSurface, directlySet);
    useEffect(() => {
        const handleSlideWidthChange = (latestSlideWidths) => {
            const hasAllSlideWidths = latestSlideWidths.length === duplicateContentLength &&
                latestSlideWidths.every((width) => width > 0);
            const allSlideWidthAreCalculated = latestSlideWidths.length === duplicateContentLength &&
                latestSlideWidths.every((width) => width > 24);
            if (hasAllSlideWidths) {
                setInitialized(true);
            }
            if (allSlideWidthAreCalculated) {
                // used for showing content when all slides width are known and not too small
                setContentLoaded(true);
                // directlySet.current = false;
                disableDirectlySetOnce();
            }
            // Default of false was causing video to not show due to opacity remaining 0 on container
            // @ts-ignore wrong. We are use `.some` to check whether content contains an item of type video.
            if (content.some((item) => (item === null || item === void 0 ? void 0 : item.type) === 'video')) {
                setContentLoaded(true);
            }
        };
        handleSlideWidthChange(slideWidths.get());
        return slideWidths.on('change', handleSlideWidthChange);
    }, [duplicateContentLength, slideWidths, disableDirectlySetOnce, content]);
    /**
     * Disable animations when using keyboard or reduced motion is enabled
     */
    useEffect(() => {
        directlySet.current = isDisabledMotion;
    }, [isDisabledMotion]);
    /**
     * On swipe or click to a side
     */
    const handleDirection = useCallback((selectedDirection) => {
        let newInternalIndex = internalIndex.get();
        if (!clampInternalIndex ||
            (canNavigateBackward.get() && selectedDirection === GalleryAnimationDirection.backward) ||
            (canNavigateForward.get() && selectedDirection === GalleryAnimationDirection.forward)) {
            // GalleryVariants.contained works differently than the other variants
            newInternalIndex += selectedDirection * (isRTL && !isContainedVariant ? -1 : 1);
        }
        // Make sure we use the last available index that doesn't exceed the maxOffset
        else if (!canNavigateForward.get() &&
            ((isRTL && selectedDirection === GalleryAnimationDirection.backward) ||
                (!isRTL && selectedDirection === GalleryAnimationDirection.forward))) {
            let offset = 0;
            const _slideWidths = slideWidths.get();
            for (let i = 0; i < _slideWidths.length; i++) {
                offset -= _slideWidths[i];
                newInternalIndex = i + 1;
                if (offset < minWrapperX.get()) {
                    break;
                }
            }
        }
        // Reset to 0 index when we can't move backwards
        else if (!canNavigateBackward.get() &&
            ((isRTL && selectedDirection === GalleryAnimationDirection.forward) ||
                (!isRTL && selectedDirection === GalleryAnimationDirection.backward))) {
            newInternalIndex = 0;
        }
        internalIndex.set(clampInternalIndex
            ? clamp(newInternalIndex, 0, originalContentLength - 1)
            : newInternalIndex);
    }, [
        slideWidths,
        minWrapperX,
        canNavigateBackward,
        canNavigateForward,
        clampInternalIndex,
        internalIndex,
        originalContentLength,
        isRTL,
        isContainedVariant,
    ]);
    /**
     * Pan handlers
     */
    const handlePanStart = useCallback((event) => {
        if (!('isPrimary' in event) || (event === null || event === void 0 ? void 0 : event.isPrimary)) {
            preventCancelableDefault(event);
            wrapperWidth.set(event.currentTarget.clientWidth);
            directlySet.current = true;
        }
    }, [wrapperWidth]);
    const handlePan = useCallback((event, info) => {
        // @ts-ignore
        if ((!('isPrimary' in event) || (event === null || event === void 0 ? void 0 : event.isPrimary)) && directlySet.current) {
            offset.set(
            // ensure that it will follow the mouse the right direction when panning
            (isRTL && !isContainedVariant ? -1 : 1) *
                (percentages ? info.offset.x / wrapperWidth.get() : info.offset.x));
            velocity.set(info.velocity.x);
        }
    }, [offset, percentages, velocity, wrapperWidth, isRTL, isContainedVariant]);
    const handlePanEnd = useCallback(() => {
        const lastOffset = offset.get();
        const lastOffsetAbs = Math.abs(lastOffset);
        directlySet.current = false;
        offset.set(0);
        const threshold = percentages
            ? initialDragThreshold
            : initialDragThreshold * wrapperWidth.get();
        const flicked = Math.abs(velocity.get()) / wrapperWidth.get() > 0.2 && lastOffsetAbs > threshold;
        dragged.current = lastOffsetAbs > threshold || flicked;
        // Percentage is used with full-width slide, so the threshold is a bit bigger (25%)
        // For the other slides we use 10% as a threshold because the slide in there should be a bit smaller
        const thresholdThreshold = dragThreshold === undefined ? (percentages ? 0.25 : 0.1 * wrapperWidth.get()) : dragThreshold;
        if (lastOffsetAbs > thresholdThreshold || flicked) {
            handleDirection(lastOffset < 0
                ? isRTL
                    ? GalleryAnimationDirection.backward
                    : GalleryAnimationDirection.forward
                : isRTL
                    ? GalleryAnimationDirection.forward
                    : GalleryAnimationDirection.backward);
        }
    }, [handleDirection, offset, percentages, velocity, wrapperWidth, dragThreshold, isRTL]);
    /**
     * Update slide offset information
     */
    const getUpdatedOffset = useCallback((latestInternalIndex) => {
        if (!initialized) {
            return Array(duplicateContentLength).fill(0);
        }
        const internalClampedIndex = isRTL
            ? latestInternalIndex >= 0
                ? // right side becomes left side
                    latestInternalIndex % duplicateContentLength
                : // left side becomes right side
                    duplicateContentLength + 1 + ((latestInternalIndex - 1) % duplicateContentLength)
            : latestInternalIndex >= 0
                ? latestInternalIndex % duplicateContentLength
                : duplicateContentLength - 1 + ((latestInternalIndex + 1) % duplicateContentLength);
        clampedIndex.set(internalClampedIndex % originalContentLength);
        // using the middle to determine the amount of items needed to fill the viewport and never see a slide move from one side to the other
        const splitOffset = Math.round(duplicateContentLength / 2);
        let rtlSplitAtPosition = duplicateContentLength - (internalClampedIndex + splitOffset);
        if (rtlSplitAtPosition < 0) {
            rtlSplitAtPosition = duplicateContentLength + rtlSplitAtPosition;
        }
        const splitAt = (isRTL ? rtlSplitAtPosition : internalClampedIndex + splitOffset) % duplicateContentLength;
        currentPage.current = Math.floor(latestInternalIndex / duplicateContentLength);
        return calculateOffset({
            slideWidths: slideWidths.get(),
            contentLength: duplicateContentLength,
            currentPage: currentPage.current,
            splitAt,
            splitOffset,
            percentages,
            createOffsetFromIndex,
            totalOffset: totalOffset(),
            isRTL,
            isContainedVariant,
        });
    }, [
        initialized,
        duplicateContentLength,
        clampedIndex,
        originalContentLength,
        slideWidths,
        percentages,
        createOffsetFromIndex,
        totalOffset,
        isRTL,
        isContainedVariant,
    ]);
    /**
     * Create array of motion values, so we can use them in slides without rerender
     *
     * When content length changes, this also resets internal indexes
     * useMemo instead of useEffect because useMemo is sync. So it runs during the render loop.
     *
     * This needs to be called BEFORE getUpdatedOffset is called in the contentOffset call. Otherwise
     * the clampedIndex is already updated with a new value and we can't compare.
     */
    const contentOffset = useMemo(() => {
        currentPage.current = 0;
        if (internalIndex.get() !== 0 || clampedIndex.get() !== 0) {
            directlySetOnce();
        }
        const highestIndex = originalContentLength - 1;
        if (clampedIndex.get() > highestIndex) {
            internalIndex.set(highestIndex);
            clampedIndex.set(highestIndex);
        }
        else if (clampedIndex.get() !== internalIndex.get()) {
            internalIndex.set(clampedIndex.get());
        }
        const newOffset = getUpdatedOffset(internalIndex.get());
        return newOffset.map((value) => mutableValue(value));
    }, [internalIndex, clampedIndex, originalContentLength, getUpdatedOffset, directlySetOnce]);
    /**
     * Update offset when index changes
     */
    useEffect(() => {
        if (!initialized) {
            return;
        }
        const handleChange = (galleryIndex) => {
            const newOffset = getUpdatedOffset(galleryIndex);
            // Update existing offset array with new values
            newOffset.forEach((value, index) => {
                contentOffset[index].set(value);
            });
        };
        handleChange(internalIndex.get());
        const internalUnsubscribe = internalIndex.on('change', (galleryIndex) => {
            handleChange(galleryIndex);
            onChange === null || onChange === void 0 ? void 0 : onChange(clampedIndex.get());
        });
        const widthsUnsubscribe = slideWidths.on('change', () => {
            handleChange(internalIndex.get());
        });
        return () => {
            internalUnsubscribe();
            widthsUnsubscribe();
        };
    }, [
        initialized,
        clampedIndex,
        contentOffset,
        getUpdatedOffset,
        internalIndex,
        onChange,
        slideWidths,
    ]);
    /**
     * Clicking on one of the sides when not dragging
     * And don't trigger navigation twice when "dragged" already set
     */
    const handleClick = useCallback((direction) => {
        if (!dragged.current && (direction === Directions.right || direction === Directions.left)) {
            // clicked on the hitarea above the arrow-buttons
            handleDirection(isRTL && variant === GalleryVariants.contained
                ? direction === Directions.right
                    ? GalleryAnimationDirection.backward
                    : GalleryAnimationDirection.forward
                : direction === Directions.right
                    ? GalleryAnimationDirection.forward
                    : GalleryAnimationDirection.backward);
        }
    }, [handleDirection, variant, isRTL]);
    /**
     * Calculate whether to animated the slides or not depending on the
     * distance between the current and new index
     */
    const handleIndicatorUpdate = useCallback((newIndex) => {
        directlySet.current = true;
        internalIndex.set(newIndex);
    }, [internalIndex]);
    const handleArrowLeftClick = useCallback(() => {
        // position of arrow is flipped in RTL
        handleClick(isRTL ? Directions.right : Directions.left);
    }, [handleClick, isRTL]);
    const handleArrowRightClick = useCallback(() => {
        // position of arrow is flipped in RTL
        handleClick(isRTL ? Directions.left : Directions.right);
    }, [handleClick, isRTL]);
    // This gets set in dialog or drawer, we want to use that instead of the body for calculations
    const { scrollElement } = useScrollContext();
    const positionRef = useRef(null);
    useEffect(() => {
        if (variant === GalleryVariants.standard || variant === GalleryVariants.highContrastChevrons) {
            return;
        }
        const handlePosition = () => {
            if (positionRef.current) {
                const dimensions = positionRef.current.getBoundingClientRect();
                const scrollElementWidth = (scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.clientWidth) || document.documentElement.clientWidth;
                const scrollElementOffset = (scrollElement || document.documentElement).getBoundingClientRect().left;
                const width = variant === GalleryVariants.contained
                    ? Math.min(scrollElementWidth, 1920) // TODO: this is the media section width
                    : dimensions.width;
                const left = variant === GalleryVariants.contained
                    ? dimensions.left - scrollElementOffset - (scrollElementWidth - width) / 2
                    : dimensions.left;
                wrapperWidth.set(width);
                positionOffset.set(left);
                directlySetOnce();
            }
        };
        handlePosition();
        window.addEventListener('resize', handlePosition);
        return () => {
            window.removeEventListener('resize', handlePosition);
        };
    }, [directlySetOnce, positionOffset, scrollElement, variant, wrapperWidth]);
    const overflowOffset = useTransform(positionOffset, (latestPositionOffset) => `-${latestPositionOffset}px`);
    const isMobileOrTablet = ((_b = useMediaQuery()) === null || _b === void 0 ? void 0 : _b.name) === 'mobile' || ((_c = useMediaQuery()) === null || _c === void 0 ? void 0 : _c.name) === 'tablet';
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: positionRef }),
        React.createElement(OverflowWrapper, { overflow: true, style: variant == GalleryVariants.contained
                ? {
                    width: wrapperWidth,
                    insetInlineStart: overflowOffset,
                }
                : {} },
            (content === null || content === void 0 ? void 0 : content.length) > 1 ? (React.createElement(PointerReplace, { onClick: handleClick, onPan: handlePan, onMouseDown: handlePanStart, onMouseUp: handlePanEnd, onMouseLeave: handlePanEnd, onTouchStart: handlePanStart, onTouchEnd: handlePanEnd, variant: variant, canNavigateBackward: canNavigateBackward, canNavigateForward: canNavigateForward },
                React.createElement(GallerySlides, { x: x, content: content, CustomComponent: CustomComponent, internalIndex: internalIndex, contentOffset: contentOffset, slideWidths: slideWidths, variant: variant, initialized: initialized, contentLoaded: contentLoaded, dragged: dragged, percentages: percentages, isDark: isDarkTheme }))) : (React.createElement(GallerySlides, { x: x, content: content, CustomComponent: CustomComponent, internalIndex: internalIndex, contentOffset: contentOffset, slideWidths: slideWidths, variant: variant, initialized: initialized, contentLoaded: contentLoaded, dragged: dragged, percentages: percentages, isDark: isDarkTheme })),
            (variant === GalleryVariants.standard ||
                variant === GalleryVariants.highContrastChevrons) &&
                content.length > 1 && (React.createElement(ThemeProvider, { theme: isDarkTheme ? Themes.dark : Themes.light },
                React.createElement(motion.div, { variants: {
                        [MotionControlsVariants.enter]: {
                            transition: {
                                staggerChildren: 0,
                            },
                        },
                    } },
                    React.createElement(ArrowLeftButton, { variants: arrowAnimationVariants, isHighContrast: variant === GalleryVariants.highContrastChevrons, "data-type": "control-button", onClick: handleArrowLeftClick },
                        React.createElement(ArrowButton, { isHighContrast: variant === GalleryVariants.highContrastChevrons, icon: Icons.chevronLeft, isLeft: true, isRTL: isRTL, size: isMobileOrTablet ? Sizes.medium : Sizes.large, pairedWithText: true, ...arrowLeftButtonProps })),
                    React.createElement(ArrowRightButton, { variants: arrowAnimationVariants, isHighContrast: variant === GalleryVariants.highContrastChevrons, "data-type": "control-button", onClick: handleArrowRightClick },
                        React.createElement(ArrowButton, { isHighContrast: variant === GalleryVariants.highContrastChevrons, icon: Icons.chevronRight, isRTL: isRTL, size: isMobileOrTablet ? Sizes.medium : Sizes.large, pairedWithText: true, ...arrowRightButtonProps })),
                    hasIndicator && variant !== GalleryVariants.highContrastChevrons && (React.createElement(IndicatorWrapper, null,
                        React.createElement(AnimatePresence, null,
                            React.createElement(GalleryIndicator, { key: "gallery-indicator", length: originalContent === null || originalContent === void 0 ? void 0 : originalContent.length, activeIndex: clampedIndex, onUpdateIndex: handleIndicatorUpdate, ariaButtonLabel: ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.indicator }))))))))));
};
export default GalleryControlled;
const OverflowWrapper = styled(motion.div, shouldNotForwardProps(false, 'overflow'))(({ overflow }) => css(css `
      height: 100%;
      overflow: hidden;
      display: flex;
    `, overflow &&
    css `
        position: relative;
      `));
const ArrowButton = styled(IconButton, shouldNotForwardProps(false, 'isHighContrast', 'isLeft', 'isRTL'))(({ isHighContrast, theme, isLeft, isRTL }) => css(css `
        span {
          transform: translate(
            ${(isLeft && !isRTL) || (!isLeft && isRTL) ? '-' : ''}${createCustomSpacing(1 / 16)}
          );
        }
        border-radius: 50%;
        position: absolute;
        color: ${theme.gallery.states.enabled.chevronColor};

        &:before {
          border-radius: 50%;
        }

        ${MediaQueries.desktop} {
          svg {
            scale: 1.5;
          }
        }

        @media (hover: hover) {
          &:hover {
            color: ${theme.gallery.states.hover.chevronColor};
          }
        }
      `, isHighContrast &&
    css `
          background-color: ${theme.gallery.states.enabled.fixedButtonColor};
          ${responsiveStyles({
        insetBlockStart: {
            mobile: `calc(50% - ${Spacings.medium})`,
            desktop: `calc(50% - ${createCustomSpacing(20 / 16)})`,
        },
        width: { mobile: Spacings.xLarge, desktop: createCustomSpacing(40 / 16) },
        height: { mobile: Spacings.xLarge, desktop: createCustomSpacing(40 / 16) },
    })}

          @media (hover: hover) {
            &:hover {
              background-color: ${theme.gallery.states.hover.fixedButtonColor};
            }
          }
        `));
const IndicatorWrapper = styled.div `
  position: absolute;
  z-index: ${LayoutIndexes.formModal};
  inset-block-end: 0;
  inset-inline-start: 0;
  ${responsiveStyles({
    marginInlineStart: { mobile: Spacings.small, desktop: Spacings.large },
})};
`;
const ArrowPositionWrapper = motion(styled.div `
  position: absolute;
  pointer-events: all;
  z-index: ${LayoutIndexes.popover};
  > button {
    /* 'all' is needed for the transform transition (this controls the bg hover color on high contrast too) */
    transition: ${createCSSTransition('all', MotionEasings.default, 200)};
  }
  > button:active {
    transform: scale(0.8);
  }
`);
const ArrowLeftButton = styled(ArrowPositionWrapper, shouldNotForwardProps(false, 'isHighContrast'))(({ isHighContrast }) => css `
    ${responsiveStyles({
    insetInlineStart: {
        mobile: isHighContrast ? Spacings.small : Spacings.none,
        desktop: isHighContrast ? Spacings.medium : Spacings.xxSmall,
    },
    height: {
        mobile: Spacings.xLarge,
        desktop: isHighContrast ? createCustomSpacing(40 / 16) : Spacings.xxLarge,
    },
    width: {
        mobile: Spacings.xLarge,
        desktop: isHighContrast ? createCustomSpacing(40 / 16) : Spacings.xxLarge,
    },
    insetBlockStart: {
        mobile: `calc(50% - ${Spacings.medium})`,
        desktop: isHighContrast
            ? `calc(50% - ${createCustomSpacing(20 / 16)})`
            : `calc(50% - ${Spacings.large})`,
    },
})}
  `);
const ArrowRightButton = styled(ArrowPositionWrapper, shouldNotForwardProps(false, 'isHighContrast'))(({ isHighContrast }) => css `
    ${responsiveStyles({
    insetInlineEnd: {
        mobile: isHighContrast ? Spacings.small : Spacings.none,
        desktop: isHighContrast ? Spacings.medium : Spacings.xxSmall,
    },
    height: {
        mobile: Spacings.xLarge,
        desktop: isHighContrast ? createCustomSpacing(40 / 16) : Spacings.xxLarge,
    },
    width: {
        mobile: Spacings.xLarge,
        desktop: isHighContrast ? createCustomSpacing(40 / 16) : Spacings.xxLarge,
    },
    insetBlockStart: {
        mobile: `calc(50% - ${Spacings.medium})`,
        desktop: isHighContrast
            ? `calc(50% - ${createCustomSpacing(20 / 16)})`
            : `calc(50% - ${Spacings.large})`,
    },
})}
  `);
